// SwipeRail.js — a horizontal shelf that works with a thumb AND a mouse.
//
// Touch users swipe; that gesture is invisible and unavailable to someone on
// a desktop with a mouse, so this adds quiet arrows on hover-capable screens
// and a soft fade at whichever edge still has content beyond it. The arrows
// disappear at the ends, so the control never lies about what's there.

import React, { useCallback, useEffect, useRef, useState } from 'react';


// Scroll a snap rail by one "page". Snap must be suspended while the glide
// runs: with `scroll-snap-type: x mandatory`, the browser pulls a programmatic
// scroll straight back to the current snap point and the arrows do nothing.
export function nudgeRail(el, dir) {
  if (!el) return;
  const step = Math.max(el.clientWidth * 0.85, 200);
  const max = el.scrollWidth - el.clientWidth;
  const target = Math.max(0, Math.min(max, el.scrollLeft + dir * step));
  const previous = el.style.scrollSnapType;
  el.style.scrollSnapType = 'none';
  el.scrollTo({ left: target, behavior: 'smooth' });
  window.setTimeout(() => { el.style.scrollSnapType = previous || ''; }, 500);
}

export default function SwipeRail({ children, className = '', gap = 'gap-3', ariaLabel = 'Scrollable options' }) {
  const ref = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(max <= 2 || el.scrollLeft >= max - 2);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, children]);

  const nudge = (dir) => nudgeRail(ref.current, dir);

  const arrow = (side, disabled) => (
    <button
      type="button"
      onClick={() => nudge(side === 'left' ? -1 : 1)}
      disabled={disabled}
      aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
      className="w-8 h-8 rounded-full flex items-center justify-center transition-all disabled:opacity-25 disabled:cursor-default hover:shadow-[0_0_14px_rgba(255,215,140,0.45)]"
      style={{ background: '#12101d', border: '1px solid rgba(184,150,79,0.5)', color: '#ffe2a8' }}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.6}
          d={side === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
        />
      </svg>
    </button>
  );

  const scrollable = !(atStart && atEnd);

  return (
    <div className="relative">
      {scrollable && (
        <div className="hidden md:flex justify-end gap-2 mb-2">
          {arrow('left', atStart)}
          {arrow('right', atEnd)}
        </div>
      )}

      {/* a whisper of shadow at the edge that still hides something */}
      {!atEnd && (
        <div
          className="hidden md:block absolute right-0 bottom-2 w-10 pointer-events-none"
          style={{ top: '2.75rem', background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.9))' }}
        />
      )}

      <div
        ref={ref}
        onScroll={measure}
        aria-label={ariaLabel}
        className={`rail flex ${gap} overflow-x-auto pb-2 -mx-1 px-1 ${className}`}
        style={{
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </div>
    </div>
  );
}
